import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [business, setBusiness] = useState(() => {
    try {
      const cached = localStorage.getItem('business');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Tək kafe üçün business_locations yoxdursa avtomatik yarat
  const ensureLocation = async (biz) => {
    if (biz.is_chain) return; // chain sahibi — filialları özü əlavə edir
    const { data: existing } = await supabase
      .from('business_locations')
      .select('id')
      .eq('business_id', biz.id)
      .maybeSingle();
    if (existing) return; // artıq var

    await supabase.from('business_locations').insert({
      business_id:       biz.id,
      name:              biz.name,
      address:           biz.address || '',
      city:              biz.city || 'Bakı',
      phone:             biz.phone || '',
      cuisine:           biz.cuisine_type || '',
      business_type:     biz.business_type || 'cafe',
      total_capacity:    biz.total_capacity || 50,
      current_occupancy: 0,
      is_open:           true,
      premium_tier:      biz.premium_tier || 1,
      rating:            0,
      target_audience:   biz.target_audience || [],
    });
  };

  // Fetch business row by auth user id
  const fetchBusiness = async (user) => {
    const { data } = await supabase
      .from('businesses')
      .select('*')
      .eq('owner_id', user.id)
      .maybeSingle();
    if (data) {
      const biz = { ...data, email: user.email };
      setBusiness(biz);
      localStorage.setItem('business', JSON.stringify(biz));
      await ensureLocation(data); // location yoxdursa avtomatik yarat
    }
  };

  useEffect(() => {
    // Check existing session on mount
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) {
        fetchBusiness(data.session.user).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setBusiness(null);
        localStorage.removeItem('business');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    const { data: authData, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: 'Email və ya şifrə səhvdir' };

    // 1. Try owner login
    const { data: biz } = await supabase
      .from('businesses')
      .select('*')
      .eq('owner_id', authData.user.id)
      .maybeSingle();

    if (biz) {
      const businessData = { ...biz, email: authData.user.email };
      setBusiness(businessData);
      localStorage.setItem('business', JSON.stringify(businessData));
      await ensureLocation(biz);
      return { success: true, role: 'owner' };
    }

    // 2. Try manager login
    const { data: bu } = await supabase
      .from('business_users')
      .select('*, businesses(*)')
      .eq('auth_user_id', authData.user.id)
      .not('accepted_at', 'is', null)
      .single();

    if (bu?.businesses) {
      const businessData = { ...bu.businesses, email: authData.user.email, _managerRole: bu.role };
      setBusiness(businessData);
      localStorage.setItem('business', JSON.stringify(businessData));
      return { success: true, role: bu.role };
    }

    await supabase.auth.signOut();
    return { success: false, error: 'Bu hesabla əlaqəli biznes tapılmadı. Müraciətiniz hələ təsdiqlənməyib.' };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setBusiness(null);
    localStorage.removeItem('business');
  };

  // Refresh local business data after profile update
  const refreshBusiness = async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) {
      const { data: biz } = await supabase
        .from('businesses')
        .select('*')
        .eq('owner_id', data.session.user.id)
        .single();
      if (biz) {
        const updated = { ...biz, email: data.session.user.email };
        setBusiness(updated);
        localStorage.setItem('business', JSON.stringify(updated));
      }
    }
  };

  return (
    <AuthContext.Provider value={{ business, loading, login, logout, refreshBusiness, isAuthenticated: !!business }}>
      {children}
    </AuthContext.Provider>
  );
};
